import { z } from "zod";
/** Watching together: one room, up to this many people at once. */
export const PARTY_MAX = 10;
export const PARTY_PUBLIC_ROOMS = [
    { code: "main-live", name: "Общий зал", seed: null },
    { code: "lounge-live", name: "Вечерний зал", seed: 35062 },
    { code: "cinema-live", name: "Кино-зал", seed: 57555 },
];
export const isPublicPartyCode = (code) => PARTY_PUBLIC_ROOMS.some((room) => room.code === code);
/** Who sees the room in the list: everyone, the host's friends, or nobody (link only). */
export const PARTY_VISIBILITY = ["public", "friends", "link"];
export const PARTY_DEFAULT_SETTINGS = {
    visibility: "public",
    stickers: true,
    gifs: true,
    reactions: true,
    allControl: false,
    waitForSlow: true,
    capQuality: true,
    systemMessages: true,
    autoNext: true,
};
export const adminPartyActionSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("close"), reason: z.string().trim().max(200).optional() }),
    z.object({ action: z.literal("kick"), userId: z.string().min(1).max(40) }),
    z.object({ action: z.literal("say"), text: z.string().trim().min(1).max(300) }),
    z.object({ action: z.literal("unchat"), messageId: z.string().min(1).max(40) }),
    z.object({ action: z.literal("pause") }),
]);
export const createPartyInputSchema = z.object({
    studio: z.literal("hon-kon").optional(),
    capacity: z.number().int().min(2).max(PARTY_MAX).default(PARTY_MAX),
    allControl: z.boolean().default(false),
    waitForSlow: z.boolean().default(true),
    autoNext: z.boolean().default(true),
    name: z.string().trim().max(80).nullable().optional(),
    animeId: z.number().int().positive(),
    episode: z.number().int().min(1).max(5000).default(1),
    visibility: z.enum(PARTY_VISIBILITY).default("public"),
});
export const partySettingsInputSchema = z.object({
    visibility: z.enum(PARTY_VISIBILITY).optional(),
    stickers: z.boolean().optional(),
    gifs: z.boolean().optional(),
    reactions: z.boolean().optional(),
    allControl: z.boolean().optional(),
    waitForSlow: z.boolean().optional(),
    capQuality: z.boolean().optional(),
    systemMessages: z.boolean().optional(),
    autoNext: z.boolean().optional(),
});
export const partyLoadingInputSchema = z.object({ loading: z.boolean() });
export const partyQueueInputSchema = z.object({ animeId: z.number().int().positive(), episode: z.number().int().min(1).max(5000).default(1) });
export const partyMuteInputSchema = z.object({ userId: z.string().min(1).max(40), muted: z.boolean() });
export const partyPlaybackInputSchema = z.object({
    sourceId: z.string().max(160).nullable().optional(),
    /** Paused because this member is still loading (it resumes when they're ready). */
    waiting: z.boolean().optional(),
    /** Another title for the whole room (anyone may switch it). */
    animeId: z.number().int().positive().optional(),
    episode: z.number().int().min(1).max(5000),
    playing: z.boolean(),
    position: z.number().min(0).max(36_000),
});
/**
 * Animated stickers: Google's Noto Animated Emoji (CC BY 4.0), by code point.
 * https://googlefonts.github.io/noto-emoji-animation/
 */
export const PARTY_STICKERS = [
    { code: "1f602", label: "Смех до слёз" },
    { code: "1f62d", label: "Рыдаю" },
    { code: "1f970", label: "Влюблённость" },
    { code: "1f60d", label: "Восторг" },
    { code: "1f929", label: "Звёзды в глазах" },
    { code: "1f92f", label: "Взрыв мозга" },
    { code: "1f631", label: "Ужас" },
    { code: "1f525", label: "Огонь" },
    { code: "2764_fe0f", label: "Сердце" },
    { code: "1f494", label: "Разбитое сердце" },
    { code: "1f44f", label: "Аплодисменты" },
    { code: "1f389", label: "Праздник" },
    { code: "1f621", label: "Злость" },
    { code: "1f914", label: "Хмм" },
    { code: "1f634", label: "Сплю" },
    { code: "1f480", label: "Я умер" },
    { code: "1f440", label: "Глаза" },
    { code: "1f97a", label: "Ну пожалуйста" },
    { code: "1f60e", label: "Круто" },
    { code: "1f60f", label: "Ухмылка" },
    { code: "1f648", label: "Не смотрю" },
    { code: "2728", label: "Искры" },
    { code: "1f37f", label: "Попкорн" },
    { code: "1fae1", label: "Честь" },
];
export function stickerUrl(code) {
    return `https://fonts.gstatic.com/s/e/notoemoji/latest/${code}/512.webp`;
}
/** The same sticker standing still — an SVG of a few KB instead of ~600 KB of animation. */
export function stickerStill(code) {
    return `https://fonts.gstatic.com/s/e/notoemoji/latest/${code}/emoji.svg`;
}
/** Emoji anyone can send — the plain ones, no PRO needed. */
export const PARTY_EMOJI = [
    "😂", "🤣", "😭", "🥹", "😍", "🥰", "😘", "😎", "🤩", "😏",
    "😱", "😳", "🤯", "😡", "🤔", "🙄", "😴", "🥱", "💀", "👀",
    "🔥", "❤️", "💔", "💯", "✨", "🎉", "👏", "🙏", "👍", "👎",
    "🍿", "😮", "🫡", "🙈", "😈", "🤝",
];
/** Only emoji (and spaces): what a member without PRO may write. */
export function emojiOnly(text) {
    const t = text.replace(/\s+/g, "");
    return t.length > 0 && t.length <= 40 && /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|[\u{1F1E6}-\u{1F1FF}]|\u200d|\ufe0f)+$/u.test(t);
}
export const partyChatInputSchema = z
    .object({
    text: z.string().trim().max(500).optional(),
    sticker: z.string().max(20).optional(),
    gif: z.string().regex(/^[\w.-]+\.webp$/).max(120).optional(),
})
    .refine((v) => [v.text, v.sticker, v.gif].filter((x) => x != null && x !== "").length === 1, { message: "One of text, sticker or gif" });
/** How fast this member's line is, for the room's quality cap. */
export const partyNetInputSchema = z.object({ mbps: z.number().min(0).max(1000).nullable() });
export const PARTY_REACTIONS = ["😂", "😭", "🔥", "❤️", "😮", "👏"];
export const partyReactionInputSchema = z.object({ emoji: z.enum(PARTY_REACTIONS) });
//# sourceMappingURL=party.js.map